'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useDarkMode } from '@/components/DarkModeProvider';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabaseClient';
import { TemplateDetailSkeleton, TemplateCardSkeleton } from '@/components/ui/SkeletonLoader';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  trackTemplateView,
  trackBuyTemplateClick,
  trackRelatedTemplateClick,
} from '@/lib/analytics';
import AskCommunityWidget from '@/app/community/components/AskCommunityWidget';
import TemplateVersionChangelog from '@/app/community/components/TemplateVersionChangelog';
import TemplateReviews from '@/app/community/components/TemplateReviews';
import { LicensingProductList } from '@/components/LicensingTextVersions';
import TemplateReviewsSection from './TemplateReviewsSection';


interface Template {
  id: string;
  name: string;
  platform: string;
  category: string | null;
  price: number;
  rating: number;
  reviews_count: number;
  downloads_count: number;
  thumbnail_url: string | null;
  description: string | null;
  tags: string[];
  updated_at: string;
  created_at: string;
}

interface Review {
  id: string;
  rating: number;
  review_text: string | null;
  created_at: string;
  user_id: string;
}

interface TemplateDetailClientProps {
  initialTemplate?: Template | null;
  initialRelated?: Template[];
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }
  return false;
}

export default function TemplateDetailClient({ initialTemplate = null, initialRelated = [] }: TemplateDetailClientProps) {
  return (
    <ErrorBoundary>
      <TemplateDetailContent initialTemplate={initialTemplate} initialRelated={initialRelated} />
    </ErrorBoundary>
  );
}

function TemplateDetailContent({ initialTemplate = null, initialRelated = [] }: TemplateDetailClientProps) {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { showSuccess, showInfo, showError } = useToast();
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const templateId = searchParams.get('id');
  
  const [template, setTemplate] = useState<Template | null>(initialTemplate);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [relatedTemplates, setRelatedTemplates] = useState<Template[]>(initialRelated);
  const [loading, setLoading] = useState(!initialTemplate);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'features' | 'reviews' | 'specs'>('overview');
  const [addedToCart, setAddedToCart] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    if (templateId) {
      if (!initialTemplate) {
        loadTemplateDetails();
      } else {
        // Template was prefetched — only load user-specific data and reviews
        loadUserDataAndReviews();
        // Track template view
        trackTemplateView(
          initialTemplate.id,
          initialTemplate.name,
          initialTemplate.platform,
          initialTemplate.price
        );
      }
    } else {
      setLoading(false);
      setError('No template ID provided. Please select a template from the catalog.');
    }
  }, [templateId, user]);

  const loadUserDataAndReviews = async () => {
    if (!templateId) return;
    try {
      if (user) {
        const { data: purchaseData } = await supabase
          .from('purchases')
          .select('id')
          .eq('buyer_id', user.id)
          .eq('template_id', templateId)
          .eq('status', 'paid')
          .maybeSingle();
        setHasPurchased(!!purchaseData);

        const { data: userReview } = await supabase
          .from('reviews')
          .select('id')
          .eq('user_id', user.id)
          .eq('template_id', templateId)
          .maybeSingle();
        setHasReviewed(!!userReview);
      }

      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('*')
        .eq('template_id', templateId)
        .order('created_at', { ascending: false })
        .limit(10);
      setReviews(reviewsData || []);
    } catch (err: any) {
      console.error('Error loading user data:', err.message);
    }
  };

  const loadTemplateDetails = async () => {
    try {
      setLoading(true);
      // Load template
      const { data: templateData, error: templateError } = await supabase
        .from('templates')
        .select('*')
        .eq('id', templateId)
        .eq('is_published', true)
        .single();

      if (templateError) {
        if (isSchemaError(templateError)) {
          console.error('Schema error:', templateError.message);
          throw templateError;
        } else {
          setError('Template not found');
          return;
        }
      }

      setTemplate(templateData);

      // Track template view
      trackTemplateView(templateData.id, templateData.name, templateData.platform, templateData.price);

      // Check if user has purchased this template
      if (user) {
        const { data: purchaseData } = await supabase
          .from('purchases')
          .select('id')
          .eq('buyer_id', user.id)
          .eq('template_id', templateId)
          .eq('status', 'paid')
          .maybeSingle();

        setHasPurchased(!!purchaseData);

        // Check if user has already reviewed
        const { data: userReview } = await supabase
          .from('reviews')
          .select('id')
          .eq('user_id', user.id)
          .eq('template_id', templateId)
          .maybeSingle();

        setHasReviewed(!!userReview);
      }

      // Load reviews
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('template_id', templateId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (reviewsError) {
        if (isSchemaError(reviewsError)) {
          console.error('Schema error:', reviewsError.message);
          throw reviewsError;
        }
      } else {
        setReviews(reviewsData || []);
      }

      // Load related templates client-side (fallback if no SSR data)
      if (initialRelated.length === 0) {
        await loadRelatedTemplates(templateData);
      }
    } catch (err: any) {
      console.error('Error loading template:', err.message);
      setError('Failed to load template details');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedTemplates = async (templateData: Template) => {
    try {
      setRelatedLoading(true);
      let related: Template[] = [];
      // Try same platform + category first
      if (templateData.platform && templateData.category) {
        const { data } = await supabase
          .from('templates')
          .select('*')
          .eq('is_published', true)
          .eq('platform', templateData.platform)
          .eq('category', templateData.category)
          .neq('id', templateData.id)
          .limit(6);
        related = data || [];
      }

      // Fall back to same platform
      if (related.length < 3 && templateData.platform) {
        const existingIds = new Set(related.map((t) => t.id));
        const { data } = await supabase
          .from('templates')
          .select('*')
          .eq('is_published', true)
          .eq('platform', templateData.platform)
          .neq('id', templateData.id)
          .limit(6);
        const extra = (data || []).filter((t) => !existingIds.has(t.id));
        related = [...related, ...extra].slice(0, 6);
      }

      // Fall back to same category
      if (related.length < 3 && templateData.category) {
        const existingIds = new Set(related.map((t) => t.id));
        const { data } = await supabase
          .from('templates')
          .select('*')
          .eq('is_published', true)
          .eq('category', templateData.category)
          .neq('id', templateData.id)
          .limit(6);
        const extra = (data || []).filter((t) => !existingIds.has(t.id));
        related = [...related, ...extra].slice(0, 6);
      }

      setRelatedTemplates(related);
    } catch (err: any) {
      console.error('Error loading related templates:', err.message);
    } finally {
      setRelatedLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!user) {
      showError('Please sign in to submit a review');
      return;
    }

    if (!hasPurchased) {
      showError('You must purchase this template before reviewing');
      return;
    }

    if (hasReviewed) {
      showError('You have already reviewed this template');
      return;
    }

    if (!reviewComment.trim()) {
      showError('Please add a comment to your review');
      return;
    }

    try {
      setSubmittingReview(true);
      const { error } = await supabase
        .from('reviews')
        .insert({
          user_id: user.id,
          template_id: templateId,
          rating: reviewRating,
          comment: reviewComment.trim(),
        });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          showError(error.message || 'Failed to submit review');
          return;
        }
      }

      showSuccess('Review submitted successfully!');
      setShowReviewForm(false);
      setReviewComment('');
      setReviewRating(5);
      setHasReviewed(true);
      
      // Reload reviews
      loadTemplateDetails();
    } catch (err: any) {
      console.error('Error submitting review:', err.message);
      showError('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAddToCart = () => {
    setAddedToCart(true);
    showSuccess('Template added to cart!');
    if (template) {
      trackBuyTemplateClick(template.id, template.name, template.platform, template.price);
    }
    setTimeout(() => setAddedToCart(false), 2500);
  };

  if (loading) {
    return (
      <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <main className="flex-1 pt-16">
          {/* Breadcrumb skeleton */}
          <div className="border-b py-3" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}>
            <div className="max-w-8xl mx-auto px-6">
              <div className="h-4 w-64 rounded animate-pulse" style={{ background: 'var(--bg-card)' }} />
            </div>
          </div>
          <TemplateDetailSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <main className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center">
            <Icon name="ExclamationCircleIcon" size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <h3 className="font-display font-medium text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
              {error || 'Template not found'}
            </h3>
            <Link href="/templates" className="btn-primary inline-flex mt-4">
              Browse Templates
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const templateImages = [
    { src: template.thumbnail_url || '/assets/images/no_image.png', alt: `${template.name} preview` },
  ];

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main className="flex-1 pt-16">
        {/* Breadcrumb */}
        <div className="border-b py-3" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}>
          <div className="max-w-8xl mx-auto px-6 flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
            <Link href="/homepage" className="hover:text-accent transition-colors">Home</Link>
            <Icon name="ChevronRightIcon" size={14} />
            <Link href="/templates" className="hover:text-accent transition-colors">Templates</Link>
            <Icon name="ChevronRightIcon" size={14} />
            <span style={{ color: 'var(--text-primary)' }}>{template.name}</span>
          </div>
        </div>

        <div className="max-w-8xl mx-auto px-6 py-10">
          <div className="grid lg:grid-cols-[1fr_380px] gap-10">
            {/* Left: Gallery + Tabs */}
            <div>
              {/* Main image */}
              <div className="rounded-2xl overflow-hidden mb-3" style={{ border: '1px solid var(--border)' }}>
                <div className="relative aspect-video">
                  <AppImage
                    src={templateImages[activeImage].src}
                    alt={templateImages[activeImage].alt}
                    fill
                    className="object-cover"
                    priority />
                </div>
              </div>

              {/* Tabs */}
              <div
                className="flex gap-1 p-1 rounded-xl mb-6"
                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                {(['overview', 'reviews', 'specs'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`tab-btn flex-1 capitalize ${activeTab === tab ? 'active' : ''}`}>
                    {tab}
                    {tab === 'reviews' && <span className="ml-1 text-2xs">({reviews.length})</span>}
                  </button>
                ))}
              </div>

              {/* Overview */}
              {activeTab === 'overview' && (
              <div>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {template.description || 'No description available.'}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {template.tags?.map((tag) => (
                  <span key={`tag-${tag}`} className="platform-tag">{tag}</span>
                  ))}
                  </div>
                </div>
              )}

              {/* Reviews */}
              {activeTab === 'reviews' && (
              <div className="space-y-4">
                  <div
                  className="flex items-center gap-6 p-5 rounded-2xl mb-6"
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                    <div className="text-center">
                      <p className="font-display text-5xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {template.rating || 0}
                      </p>
                      <div className="flex items-center gap-0.5 justify-center my-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                      <Icon key={s} name="StarIcon" size={14} variant="solid" className="star-filled" />
                      ))}
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{reviews.length} reviews</p>
                    </div>
                  </div>

                  {/* Review Submission Form */}
                  {user && hasPurchased && !hasReviewed && (
                    <div className="p-5 rounded-2xl mb-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                      {!showReviewForm ? (
                        <button
                          onClick={() => setShowReviewForm(true)}
                          className="btn-primary w-full">
                          <Icon name="PencilIcon" size={16} />
                          Write a Review
                        </button>
                      ) : (
                        <div className="space-y-4">
                          <h3 className="font-display font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
                            Write Your Review
                          </h3>
                          
                          {/* Rating Selection */}
                          <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                              Rating
                            </label>
                            <div className="flex items-center gap-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  onClick={() => setReviewRating(star)}
                                  className="transition-transform hover:scale-110">
                                  <Icon
                                    name="StarIcon"
                                    size={32}
                                    variant={star <= reviewRating ? 'solid' : 'outline'}
                                    className={star <= reviewRating ? 'star-filled' : 'star-empty'}
                                  />
                                </button>
                              ))}
                              <span className="ml-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                {reviewRating} / 5
                              </span>
                            </div>
                          </div>

                          {/* Comment */}
                          <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                              Your Review
                            </label>
                            <textarea
                              value={reviewComment}
                              onChange={(e) => setReviewComment(e.target.value)}
                              placeholder="Share your experience with this template..."
                              rows={4}
                              className="w-full px-4 py-3 rounded-xl text-sm resize-none"
                              style={{
                                background: 'var(--bg-secondary)',
                                border: '1px solid var(--border)',
                                color: 'var(--text-primary)',
                              }}
                            />
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-3">
                            <button
                              onClick={handleSubmitReview}
                              disabled={submittingReview || !reviewComment.trim()}
                              className="btn-primary flex-1">
                              {submittingReview ? 'Submitting...' : 'Submit Review'}
                            </button>
                            <button
                              onClick={() => {
                                setShowReviewForm(false);
                                setReviewComment('');
                                setReviewRating(5);
                              }}
                              disabled={submittingReview}
                              className="btn-secondary">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Purchase Required Message */}
                  {user && !hasPurchased && (
                    <div className="p-5 rounded-2xl mb-6" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-3">
                        <Icon name="InformationCircleIcon" size={20} style={{ color: 'var(--accent)' }} />
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          Purchase this template to leave a review
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Already Reviewed Message */}
                  {user && hasReviewed && (
                    <div className="p-5 rounded-2xl mb-6" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-3">
                        <Icon name="CheckCircleIcon" size={20} style={{ color: 'var(--accent)' }} />
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          Thank you for your review!
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Sign In Message */}
                  {!user && (
                    <div className="p-5 rounded-2xl mb-6" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-3">
                        <Icon name="InformationCircleIcon" size={20} style={{ color: 'var(--accent)' }} />
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <Link href="/sign-up-login" className="text-accent hover:underline">Sign in</Link> to leave a review
                        </p>
                      </div>
                    </div>
                  )}

                  {reviews.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No reviews yet</p>
                    </div>
                  ) : (
                    reviews.map((r) => (
                    <div key={r.id} className="p-5 rounded-2xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                        <div className="flex items-start gap-3 mb-3">
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div><p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>User</p></div>
                              <div className="flex items-center gap-0.5">
                                {[1, 2, 3, 4, 5].map((s) => (<Icon key={s} name="StarIcon" size={12} variant={s <= r.rating ? 'solid' : 'outline'} className={s <= r.rating ? 'star-filled' : 'star-empty'} />))}
                              </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{r.review_text || 'No comment'}</p>
                        <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>{new Date(r.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      </div>
                  ))
                  )}

                  {/* Phase E: Community-Driven Reviews (login required) */}
                  {user && templateId && (
                    <div className="mt-8 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
                      <TemplateReviews templateId={templateId} userId={user.id} />
                    </div>
                  )}
                </div>
              )}

              {/* Specs */}
              {activeTab === 'specs' && (
              <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                  <div className="flex items-center justify-between px-5 py-3.5 text-sm" style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
                    <span className="font-medium" style={{ color: 'var(--text-muted)' }}>Platform</span>
                    <span style={{ color: 'var(--text-primary)' }}>{template.platform.charAt(0).toUpperCase() + template.platform.slice(1)}</span>
                  </div>
                  <div className="flex items-center justify-between px-5 py-3.5 text-sm" style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                    <span className="font-medium" style={{ color: 'var(--text-muted)' }}>Version</span>
                    <span style={{ color: 'var(--text-primary)' }}>{template.version || '1.0'}</span>
                  </div>
                  <div className="flex items-center justify-between px-5 py-3.5 text-sm" style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
                    <span className="font-medium" style={{ color: 'var(--text-muted)' }}>Last Updated</span>
                    <span style={{ color: 'var(--text-primary)' }}>{new Date(template.updated_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between px-5 py-3.5 text-sm" style={{ background: 'var(--bg-secondary)' }}>
                    <span className="font-medium" style={{ color: 'var(--text-muted)' }}>Downloads</span>
                    <span style={{ color: 'var(--text-primary)' }}>{template.downloads_count || 0}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Purchase card */}
            <div>
              <div className="rounded-2xl p-6 sticky top-20" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="font-display text-4xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                    ${template.price}
                  </span>
                </div>

                <button
                  onClick={handleAddToCart}
                  className="btn-primary w-full mb-3"
                  disabled={addedToCart}>
                  <Icon name="ShoppingCartIcon" size={16} />
                  {addedToCart ? 'Added to Cart!' : 'Add to Cart'}
                </button>

                {template.demo_url && (
                  <a
                    href={template.demo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary w-full">
                    <Icon name="EyeIcon" size={16} />
                    Live Preview
                  </a>
                )}

                <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
                  <div className="flex items-center gap-3 mb-3">
                    <Icon name="CheckCircleIcon" size={20} style={{ color: 'var(--accent)' }} />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Lifetime updates</span>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <Icon name="CheckCircleIcon" size={20} style={{ color: 'var(--accent)' }} />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>6 months support</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Icon name="CheckCircleIcon" size={20} style={{ color: 'var(--accent)' }} />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Commercial license</span>
                  </div>
                </div>

                {/* Licensing Product List */}
                <div className="mt-6 pt-6" style={{ borderTop: '1px solid var(--border)' }}>
                  <p className="text-xs font-semibold mb-3 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                    License Details
                  </p>
                  <LicensingProductList />
                </div>

                {/* Phase E: Template Version Changelog */}
                {templateId && (
                  <div className="mt-4">
                    <TemplateVersionChangelog templateId={templateId} />
                  </div>
                )}

                {/* Template Reviews */}
                {templateId && (
                  <div className="mt-4">
                    <TemplateReviewsSection templateId={templateId} />
                  </div>
                )}

                {/* Phase E: Ask the Community Widget */}
                <div className="mt-4">
                  <AskCommunityWidget
                    templateName={template.name}
                    templateId={template.id}
                    categoryName={template.category || undefined}
                    isLoggedIn={!!user}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Related Templates */}
          <section className="mt-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-semibold text-2xl" style={{ color: 'var(--text-primary)' }}>
                Related Templates
              </h2>
              {relatedTemplates.length > 0 && (
                <Link
                  href={`/templates?platform=${template.platform}`}
                  className="text-sm font-medium transition-colors"
                  style={{ color: 'var(--accent)' }}>
                  View all {template.platform.charAt(0).toUpperCase() + template.platform.slice(1)} templates →
                </Link>
              )}
            </div>

            {relatedLoading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <TemplateCardSkeleton key={i} />
                ))}
              </div>
            ) : relatedTemplates.length === 0 ? (
              <div
                className="text-center py-10 rounded-2xl"
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <Icon name="RectangleStackIcon" size={36} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  No related templates found
                </p>
                <Link href="/templates" className="btn-secondary inline-flex mt-4 text-sm">
                  Browse all templates
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedTemplates.slice(0, 6).map((t) => (
                  <Link
                    key={t.id}
                    href={`/template-detail?id=${t.id}`}
                    onClick={() => trackRelatedTemplateClick(t.id, t.name, template.id)}
                    className="group rounded-2xl overflow-hidden transition-all hover:shadow-xl hover:-translate-y-0.5"
                    style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                    <div className="relative aspect-video overflow-hidden">
                      <AppImage
                        src={t.thumbnail_url || '/assets/images/no_image.png'}
                        alt={`${t.name} preview`}
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-105" />
                      {t.is_featured && (
                        <span
                          className="absolute top-2 left-2 px-2 py-1 rounded-lg text-2xs font-semibold"
                          style={{ background: 'var(--accent)', color: '#FFFFFF' }}>
                          Featured
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-display font-medium text-base" style={{ color: 'var(--text-primary)' }}>
                          {t.name}
                        </h3>
                        <span
                          className="px-2 py-0.5 rounded text-2xs font-semibold flex-shrink-0"
                          style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
                          {t.platform.charAt(0).toUpperCase() + t.platform.slice(1)}
                        </span>
                      </div>
                      {t.category && (
                        <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                          {t.category}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-display font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
                          ${t.price}
                        </span>
                        <div className="flex items-center gap-1">
                          <Icon name="StarIcon" size={12} variant="solid" className="star-filled" />
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {t.rating || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}